import React, { useEffect, useRef, useCallback, useContext } from "react";
import L, { LatLngExpression, marker } from "leaflet";

import "leaflet/dist/leaflet.css";
import styles from "./Map.module.css";

import { MapContext } from "../contexts/MapContextProvider";

type RouteResponse = {
  code: string;
  routes: { distance: string; duration: string; geometry: any }[];
  waypoints: any[];
};

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const ATTRIBUTION = `Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>`;
const MAPBOX_URL =
  "https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}";

function Map({ setShowBanner }: { setShowBanner: (input: string) => void }) {
  const mapRef = useRef<L.Map | null>(null);
  const routeLayer = useRef<L.FeatureGroup>(L.featureGroup());
  const markerLayer = useRef(L.featureGroup());

  const {
    setLoading,
    findAlternatives,
    travelType,
    setRouteInfo,
    markers,
    setMarkers,
    setFromInput,
    setToInput,
    animate,
    showSuggestPanel,
    setShowSuggestPanel,
  } = useContext(MapContext);

  const calculateRoute = useCallback(async () => {
    routeLayer.current.clearLayers();
    if (markers!.length < 2) {
      return;
    }

    // if (!Array.isArray(markers![0])) {
    //   return;
    // }

    setLoading!(true);

    let coordString = "";
    markers!.forEach((marker, i) => {
      coordString += `${marker[1]},${marker[0]}`;
      if (i < markers!.length - 1) {
        coordString += ";";
      }
    });

    try {
      const raw = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/${
          travelType === "car"
            ? "driving"
            : travelType === "bike"
            ? "cycling"
            : "walking"
        }/${coordString}?geometries=geojson&alternatives=${findAlternatives}&overview=full&annotations=duration,distance&access_token=${
          process.env.REACT_APP_MAPBOX_TOKEN
        }`
      );

      // const raw = await fetch(
      //   `https://routing.openstreetmap.de/routed-${travelType}/route/v1/driving/${coordString}?geometries=geojson&alternatives=${
      //     findAlternatives ? 1 : 0
      //   }`
      // );

      const data: RouteResponse = await raw.json();
      // console.log("data: ", data);

      routeLayer.current.clearLayers();

      setRouteInfo!({
        distance: parseInt(data.routes[0].distance),
        duration: parseInt(data.routes[0].duration),
      });

      if (animate) {
        const coordinates = data.routes[0].geometry.coordinates;

        data.routes[0].geometry.coordinates = [coordinates[0]];

        let i = 0;
        const timer = window.setInterval(function() {
          if (i < coordinates.length) {
            data.routes[0].geometry.coordinates.push(coordinates[i]);
            routeLayer.current.clearLayers();

            routeLayer.current.addLayer(
              L.geoJSON(data.routes[0].geometry, {
                style: { color: "red" },
              })
            );

            i++;
          } else {
            window.clearInterval(timer);
          }
        }, 50);
      } else {
        data.routes.forEach((route, i) => {
          if (i < 2) {
            if (i > 0) {
              routeLayer.current.addLayer(
                L.geoJSON(route.geometry, {
                  style: {},
                })
              );
            } else {
              routeLayer.current.addLayer(
                L.geoJSON(route.geometry, {
                  style: { color: "red" },
                })
              );
            }
          }
        });
      }

      routeLayer.current.addTo(mapRef.current as L.Map);
    } catch (error) {
      setShowBanner("Failed to fetch");
      // console.log("error: ", error.message);
    }
    fitBounds();
    setLoading!(false);
  }, [
    animate,
    findAlternatives,
    markers,
    setLoading,
    setRouteInfo,
    setShowBanner,
    travelType,
  ]);

  const reverseGeoCode = useCallback(
    async (coord: [number, number][]) => {
      // if (!Array.isArray(markers![0])) {
      //   return;
      // }
      try {
        const promises = coord.map((coor) =>
          fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${coor[1]},${coor[0]}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
          )
        );
        const raw = await Promise.allSettled(promises);
        // console.log(raw);
        const response = await Promise.all(
          raw.map((res) =>
            res.status === "fulfilled" ? res.value!.json() : null
          )
        );
        // console.log(response);

        response.forEach((res, i) => {
          if (i === 0) {
            setFromInput!(res.features[0].place_name ?? markers![0].toString());
          } else {
            setToInput!(res.features[0].place_name ?? markers![1].toString());
          }
        });
      } catch (error) {
        setShowBanner("Failed to fetch");
        // console.log("Error: ", error.message);
      }
    },
    [markers, setFromInput, setShowBanner, setToInput]
  );

  function fitBounds() {
    if (markerLayer.current.getLayers().length === 1) {
      mapRef.current!.flyToBounds(markerLayer.current.getBounds(), {
        padding: [120, 120],
        maxZoom: 8,
      });
    } else {
      mapRef.current!.flyToBounds(markerLayer.current.getBounds(), {
        padding: [120, 120],
        // maxZoom: 7,
      });
    }
  }

  useEffect(() => {
    const commonTileLayerOptions = {
      tileSize: 512,
      zoomOffset: -1,
      minZoom: 1,
      maxZoom: 18,
      attribution: ATTRIBUTION,
      accessToken: process.env.REACT_APP_MAPBOX_TOKEN,
    };
    const light = L.tileLayer(MAPBOX_URL, {
      id: "mapbox/light-v10",
      ...commonTileLayerOptions,
    });
    const dark = L.tileLayer(MAPBOX_URL, {
      id: "mapbox/dark-v10",
      ...commonTileLayerOptions,
    });
    const satellite = L.tileLayer(MAPBOX_URL, {
      id: "mapbox/satellite-v9",
      ...commonTileLayerOptions,
    });

    mapRef.current = L.map("map", {
      center: [-85.875, 40.75].reverse() as LatLngExpression,
      zoom: 4,
      layers: [light, dark],
      zoomControl: false,
    });

    const tileLayers = {
      Dark: dark,
      Light: light,
      Satellite: satellite,
    };
    L.control
      .layers(tileLayers, undefined, { position: "topright" })
      .addTo(mapRef.current);

    markerLayer.current.addTo(mapRef.current as L.Map);
  }, []);

  useEffect(() => {
    markerLayer.current.clearLayers();

    markers!.forEach((marker, i) => {
      markerLayer.current.addLayer(
        L.marker(marker, { draggable: true }).on("dragend", function(e) {
          let tempMarkers = [...(markers as [number, number][])];
          tempMarkers![i] = [
            e.target.getLatLng().lat,
            e.target.getLatLng().lng,
          ];

          setMarkers!(tempMarkers);
        })
      );
    });
  }, [markers, setMarkers]);

  useEffect(() => {
    mapRef.current!.off("mousedown");
    mapRef.current!.on("mousedown", function(e) {
      showSuggestPanel && setShowSuggestPanel!(null);
    });
  }, [setShowSuggestPanel, showSuggestPanel]);

  useEffect(() => {
    calculateRoute();
  }, [calculateRoute]);

  useEffect(() => {
    reverseGeoCode(markers!);
    markerLayer.current.getLayers().length === 1 && fitBounds();
  }, [markers, reverseGeoCode]);

  // console.log("Map");

  return <div id="map" className={styles.container}></div>;
}

export default React.memo(Map);
